import React, { useState, useEffect, useCallback } from 'react';
import { COMPANY, KTT_CODE, KTT_EMAIL } from '../utils/keyGenerator';

const KEY_SCOPES = [
  { id: 'wallet.read', label: 'Wallet Read', desc: 'View wallet balance and info' },
  { id: 'wallet.transfer', label: 'Wallet Transfer', desc: 'Send funds from wallet' },
  { id: 'transactions.read', label: 'Transactions Read', desc: 'View transaction history' },
  { id: 'swift.send', label: 'SWIFT Send', desc: 'Initiate SWIFT messages (KTT/POBO)' },
  { id: 'rtgs.settle', label: 'RTGS Settle', desc: 'Submit RTGS settlements' },
  { id: 'deposit.create', label: 'Deposit Create', desc: 'Credit funds via ledger deposit' },
  { id: 'webhook.manage', label: 'Webhook Manage', desc: 'Create and manage webhooks' },
];

function maskKey(key) {
  if (!key || key.length < 12) return key;
  return key.slice(0, 8) + '\u2022'.repeat(24) + key.slice(-6);
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function APIKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['wallet.read', 'transactions.read']);
  const [newKeyExpiry, setNewKeyExpiry] = useState('90');
  const [createdKey, setCreatedKey] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);

  const fetchKeys = useCallback(() => {
    fetch('/api/keys')
      .then(r => r.ok ? r.json() : [])
      .then(data => { setKeys(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const resp = await fetch('/api/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newKeyName.trim(),
          scopes: newKeyScopes,
          expiresInDays: parseInt(newKeyExpiry) || 90,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setCreatedKey(data.key);
        setNewKeyName('');
        setNewKeyScopes(['wallet.read', 'transactions.read']);
        setShowCreate(false);
        fetchKeys();
      }
    } catch {}
  };

  const handleRevoke = async (keyId) => {
    try {
      await fetch(`/api/keys/${keyId}`, { method: 'DELETE' });
      setRevokeConfirm(null);
      fetchKeys();
    } catch {}
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleScope = (scope) => {
    setNewKeyScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  return (
    <div className="gap-16">
      {/* Header */}
      <div className="grid2">
        <div className="card">
          <div className="card-body" style={{ padding: '28px 32px' }}>
            <div style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--text3)', fontWeight: 700, marginBottom: 12 }}>
              API CREDENTIALS
            </div>
            <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: 22, color: 'var(--navy)', fontWeight: 700 }}>
              API Keys & Authentication
            </div>
            <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13, lineHeight: 1.6 }}>
              Manage API keys for programmatic access to the KTT Platform.
              Connect external wallets, payment systems, ERPs, and custom integrations.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-body" style={{ padding: '28px 32px' }}>
            <div style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--text3)', fontWeight: 700, marginBottom: 12 }}>
              PLATFORM IDENTITY
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                ['Platform', 'KTT Payment Gateway'],
                ['Company', COMPANY],
                ['KTT Code', KTT_CODE],
                ['Email', KTT_EMAIL],
                ['Base URL', 'https://dwingateway.live/api'],
                ['Protocol', 'REST / JSON / ISO 20022'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ color: 'var(--text3)' }}>{k}</span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)', fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Created Key Display (shown once after creation) */}
      {createdKey && (
        <div className="card" style={{ border: '2px solid var(--gold)', background: 'var(--gold-soft)' }}>
          <div className="card-body" style={{ padding: '28px 32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', fontFamily: "'Source Serif 4', serif" }}>
                  Key Created Successfully
                </div>
                <div style={{ fontSize: 12, color: 'var(--coral)', fontWeight: 600, marginTop: 4 }}>
                  Save these credentials now. The Secret Key will NOT be shown again.
                </div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => setCreatedKey(null)}>Dismiss</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* API Key */}
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 18px' }}>
                <div style={{ fontSize: 10, letterSpacing: '0.15em', color: 'var(--text3)', fontWeight: 700, marginBottom: 6 }}>API KEY (Public)</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{ flex: 1, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: 'var(--navy)', wordBreak: 'break-all' }}>
                    {createdKey.apiKey}
                  </code>
                  <button className="btn btn-outline btn-sm" onClick={() => copyToClipboard(createdKey.apiKey, 'apiKey')}>
                    {copiedField === 'apiKey' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Secret Key */}
              <div style={{ background: '#fff', border: '2px solid var(--coral)', borderRadius: 'var(--radius)', padding: '14px 18px' }}>
                <div style={{ fontSize: 10, letterSpacing: '0.15em', color: 'var(--coral)', fontWeight: 700, marginBottom: 6 }}>SECRET KEY (Private - Save Now!)</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{ flex: 1, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: 'var(--navy)', wordBreak: 'break-all' }}>
                    {createdKey.secretKey}
                  </code>
                  <button className="btn btn-outline btn-sm" onClick={() => copyToClipboard(createdKey.secretKey, 'secretKey')}>
                    {copiedField === 'secretKey' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Key ID */}
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 18px' }}>
                <div style={{ fontSize: 10, letterSpacing: '0.15em', color: 'var(--text3)', fontWeight: 700, marginBottom: 6 }}>KEY ID</div>
                <code style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--text2)' }}>{createdKey.keyId}</code>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          {keys.length} API key{keys.length !== 1 ? 's' : ''} configured
        </div>
        <button className="btn btn-gold" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ Create API Key'}
        </button>
      </div>

      {/* Create Key Form */}
      {showCreate && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Create New API Key</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Key Name *</label>
                  <input className="form-input" value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                    placeholder="e.g. Production ERP, Mobile App, External Wallet" />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiration</label>
                  <select className="form-input" value={newKeyExpiry} onChange={e => setNewKeyExpiry(e.target.value)}>
                    <option value="30">30 Days</option>
                    <option value="90">90 Days</option>
                    <option value="180">180 Days</option>
                    <option value="365">1 Year</option>
                    <option value="0">Never Expires</option>
                  </select>
                </div>
              </div>

              {/* Scopes */}
              <div>
                <div className="form-label" style={{ marginBottom: 10 }}>Permissions / Scopes</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {KEY_SCOPES.map(scope => (
                    <div key={scope.id}
                      onClick={() => toggleScope(scope.id)}
                      style={{
                        padding: '12px 16px', borderRadius: 'var(--radius)', cursor: 'pointer',
                        border: newKeyScopes.includes(scope.id) ? '2px solid var(--gold)' : '1px solid var(--border)',
                        background: newKeyScopes.includes(scope.id) ? 'var(--gold-soft)' : 'var(--surface)',
                        transition: 'all 0.15s',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 18, height: 18, borderRadius: 4,
                          border: newKeyScopes.includes(scope.id) ? '2px solid var(--gold)' : '2px solid var(--border)',
                          background: newKeyScopes.includes(scope.id) ? 'var(--gold)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}>
                          {newKeyScopes.includes(scope.id) ? '\u2713' : ''}
                        </span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{scope.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{scope.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button className="btn btn-gold btn-lg" onClick={handleCreate} disabled={!newKeyName.trim() || newKeyScopes.length === 0}>
                Generate API Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Keys List */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Active API Keys</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
          ) : keys.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }}>{'\u26BF'}</div>
              <div style={{ color: 'var(--text2)', fontSize: 14, fontWeight: 500 }}>No API keys yet</div>
              <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 4 }}>Create your first key to connect external systems</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>API Key</th>
                  <th>Scopes</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Last Used</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => {
                  const expired = key.expiresAt && new Date(key.expiresAt) < new Date();
                  return (
                    <tr key={key.keyId}>
                      <td style={{ fontWeight: 600 }}>{key.name}</td>
                      <td>
                        <code style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>
                          {maskKey(key.apiKey)}
                        </code>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {key.scopes.slice(0, 3).map(s => (
                            <span key={s} style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                              borderRadius: 4, background: 'var(--gold-glow)', color: 'var(--gold)', border: '1px solid rgba(218,165,32,0.2)',
                            }}>{s.split('.')[0]}</span>
                          ))}
                          {key.scopes.length > 3 && <span style={{ fontSize: 10, color: 'var(--text3)' }}>+{key.scopes.length - 3}</span>}
                        </div>
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDate(key.createdAt)}</td>
                      <td style={{ fontSize: 11, color: expired ? 'var(--coral)' : 'var(--text3)' }}>
                        {key.expiresAt ? formatDate(key.expiresAt) : 'Never'}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${expired ? 'badge-debit' : key.revoked ? 'badge-debit' : 'badge-credit'}`}>
                          {expired ? 'EXPIRED' : key.revoked ? 'REVOKED' : 'ACTIVE'}
                        </span>
                      </td>
                      <td>
                        {revokeConfirm === key.keyId ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-danger btn-sm" onClick={() => handleRevoke(key.keyId)}>Confirm</button>
                            <button className="btn btn-outline btn-sm" onClick={() => setRevokeConfirm(null)}>Cancel</button>
                          </div>
                        ) : (
                          !key.revoked && <button className="btn btn-outline btn-sm" onClick={() => setRevokeConfirm(key.keyId)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* API Documentation */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Integration Guide</span>
        </div>
        <div className="card-body">
          <div className="grid2">
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.15em', color: 'var(--gold)', fontWeight: 700, marginBottom: 10 }}>AUTHENTICATION</div>
              <div className="code-block">{`// Include in all API requests:
Headers:
  X-API-Key: ktt_live_xxxxxxxxxxxx
  X-API-Secret: ktt_sk_xxxxxxxxxxxx
  Content-Type: application/json

// Example: Get wallet balance
GET /api/wallet
X-API-Key: ktt_live_xxxxxxxxxxxx
X-API-Secret: ktt_sk_xxxxxxxxxxxx`}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.15em', color: 'var(--gold)', fontWeight: 700, marginBottom: 10 }}>AVAILABLE ENDPOINTS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  ['GET', '/api/wallet', 'Get wallet balance'],
                  ['GET', '/api/transactions', 'List all transactions'],
                  ['POST', '/api/send', 'Send via API transfer'],
                  ['POST', '/api/ktt/send', 'Send via KTT SWIFT'],
                  ['POST', '/api/rtgs/settle', 'Submit RTGS settlement'],
                  ['POST', '/api/pobo/payout', 'POBO SWIFT payout'],
                  ['POST', '/api/deposit', 'Deposit via ledger'],
                  ['GET', '/api/health', 'Platform health check'],
                  ['POST', '/api/key-test', 'Compute key test value'],
                ].map(([method, path, desc]) => (
                  <div key={path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, minWidth: 36, textAlign: 'center',
                      background: method === 'GET' ? 'var(--teal-glow)' : 'var(--gold-glow)',
                      color: method === 'GET' ? 'var(--teal)' : 'var(--gold)',
                      border: `1px solid ${method === 'GET' ? 'rgba(13,148,136,0.2)' : 'rgba(218,165,32,0.2)'}`,
                    }}>{method}</span>
                    <code style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--navy)' }}>{path}</code>
                    <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <hr className="divider" />

          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', color: 'var(--gold)', fontWeight: 700, marginBottom: 10 }}>CODE EXAMPLES</div>
            <div className="grid2">
              <div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, fontWeight: 600 }}>Node.js / JavaScript</div>
                <div className="code-block">{`const resp = await fetch(
  'https://dwingateway.live/api/send',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': 'ktt_live_xxx',
      'X-API-Secret': 'ktt_sk_xxx',
    },
    body: JSON.stringify({
      amount: 1000,
      currency: 'USD',
      beneficiary: 'Beneficiary name',
      accountNo: 'Account number',
      bankName: 'Receiver bank name',
      bankBIC: 'RECVBIC1XXX',
    }),
  }
);
const data = await resp.json();`}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, fontWeight: 600 }}>Python</div>
                <div className="code-block">{`import requests

resp = requests.post(
  'https://dwingateway.live/api/send',
  headers={
    'X-API-Key': 'ktt_live_xxx',
    'X-API-Secret': 'ktt_sk_xxx',
  },
  json={
    'amount': 1000,
    'currency': 'USD',
    'beneficiary': 'Beneficiary name',
    'accountNo': 'Account number',
    'bankName': 'Receiver bank name',
    'bankBIC': 'RECVBIC1XXX',
  }
)
data = resp.json()`}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
